import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import { Observable } from 'rxjs';
import { CommissionResponse, ProjectSellerDto, UpsertCommissionDto } from '../../sdk/sdk';
import { CommissionService } from '../../services/commission.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';

@Component({
  selector: 'app-commission-edit-modal',
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './commission-edit-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissionEditModalComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly commissionService = inject(CommissionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = signal('');
  readonly commission = signal<CommissionResponse | null>(null);
  readonly branchId = signal<string | null>(null);
  readonly sellerName = signal<string | null>(null);
  readonly adjustedSale = signal<number | null>(null);

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly salespeople = signal<ProjectSellerDto[]>([]);
  readonly salespeopleError = signal<string | null>(null);

  readonly isSelfGen = signal(false);

  readonly isEdit = computed(() => this.commission() !== null);

  readonly title = computed(() => (this.isEdit() ? 'Edit Commission' : 'Add Commission'));

  // Cancel first, then the primary — the ordering the rest of the groundworks-modal-footer call
  // sites use. Save carries the same gate the old inline submit button did.
  readonly footerActions = computed<ModalFooterAction[]>(() => [
    {
      label: 'Cancel',
      type: 'outline-secondary',
      action: () => this.cancel(),
    },
    {
      label: 'Save',
      type: 'primary',
      action: () => this.submit(),
      loading: () => this.saving(),
      disabled: () => this.form.invalid || this.saving(),
    },
  ]);

  readonly form = this.formBuilder.nonNullable.group({
    userId: ['', Validators.required],
    percent: [0, [Validators.required, Validators.min(0)]],
    amount: [0, [Validators.required, Validators.min(0)]],
  });

  private updatingFromPercent = false;
  private updatingFromAmount = false;

  constructor() {
    effect(() => {
      const existing = this.commission();
      if (existing) {
        const adj = this.adjustedSale();
        const pct = adj && adj > 0 && existing.amount != null
          ? this.bankersRound((existing.amount / adj) * 100, 2)
          : 0;
        this.form.reset({
          userId: existing.userId ?? '',
          percent: pct,
          amount: existing.amount ?? 0,
        });
      } else {
        this.form.reset({ userId: '', percent: 0, amount: 0 });
      }
    });

    this.form.controls.percent.valueChanges.subscribe((pct) => {
      if (this.updatingFromAmount) return;
      this.updatingFromPercent = true;
      const adj = this.adjustedSale();
      if (adj != null && adj > 0) {
        const amount = this.bankersRound((pct / 100) * adj, 2);
        this.form.controls.amount.setValue(amount, { emitEvent: false });
      }
      this.updatingFromPercent = false;
    });

    this.form.controls.amount.valueChanges.subscribe((amount) => {
      if (this.updatingFromPercent) return;
      this.updatingFromAmount = true;
      const adj = this.adjustedSale();
      if (adj != null && adj > 0) {
        const pct = this.bankersRound((amount / adj) * 100, 2);
        this.form.controls.percent.setValue(pct, { emitEvent: false });
      }
      this.updatingFromAmount = false;
    });

    effect(() => {
      const projectId = this.projectId();
      if (this.isEdit() || !projectId) {
        this.salespeople.set([]);
        this.salespeopleError.set(null);
        return;
      }

      this.commissionService
        .getProjectSellers(projectId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (sellers) => {
            this.salespeople.set(sellers);
            this.salespeopleError.set(null);
          },
          error: (err: unknown) => {
            this.salespeople.set([]);
            this.salespeopleError.set(commissionErrorMessage(err));
          },
        });
    });
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    const value = this.form.getRawValue();
    const existing = this.commission();
    const dto = new UpsertCommissionDto({
      projectId: existing?.projectId ?? this.projectId(),
      userId: value.userId,
      amount: value.amount,
    });

    const request$: Observable<unknown> = existing
      ? this.commissionService.update(existing.id ?? '', dto)
      : this.commissionService.create(dto);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.errorMessage.set(commissionErrorMessage(err));
      },
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  private bankersRound(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    const shifted = value * factor;
    const truncated = Math.trunc(shifted);
    const remainder = Math.abs(shifted - truncated);

    if (Math.abs(remainder - 0.5) < 1e-10) {
      return (truncated % 2 === 0 ? truncated : truncated + Math.sign(shifted)) / factor;
    }
    return Math.round(shifted) / factor;
  }
}
